import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLoading } from '../context/LoadingContext';
import {
  ChatBubbleBottomCenterTextIcon,
  PhotoIcon,
  DocumentTextIcon,
  CodeBracketIcon,
  BoltIcon,
  ShieldCheckIcon,
  CommandLineIcon,
  ArrowRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    name: 'AI Chat',
    description: 'Engage in natural conversations with our advanced AI assistant.',
    icon: ChatBubbleBottomCenterTextIcon,
    link: '/chat',
    color: 'blue',
    badge: 'Popular',
  },
  {
    name: 'Image Generation',
    description: 'Create stunning visuals from text descriptions.',
    icon: PhotoIcon,
    link: '/image-generation',
    color: 'purple',
    badge: 'Popular',
  },

  {
    name: 'HD Photo Converter',
    description: 'Create stunning AI-generated images in high resolution',
    icon: PhotoIcon,
    link: '/photo-hd',
    color: 'red',
    badge: 'New',
  },
  {
    name: 'Text Generation',
    description: 'Generate high-quality articles, stories, and content.',
    icon: DocumentTextIcon,
    link: '/text-generation',
    color: 'green',
    badge: 'New',
  },
  {
    name: 'Code Generation',
    description: 'Get AI assistance for coding and development tasks.',
    icon: CodeBracketIcon,
    link: '/code-generation',
    color: 'pink',
    badge: 'Beta',
  },
];

const benefits = [
  {
    name: 'Lightning Fast',
    description: 'Get instant responses with our optimized AI models.',
    icon: BoltIcon,
    color: 'yellow',
  },
  {
    name: 'Secure & Private',
    description: 'Your data is encrypted and protected at all times.',
    icon: ShieldCheckIcon,
    color: 'green',
  },
  {
    name: 'Developer Friendly',
    description: 'Built with developers in mind, for seamless integration.',
    icon: CommandLineIcon,
    color: 'blue',
  },
];

const Landing: React.FC = () => {
  const { setLoading } = useLoading();

  useEffect(() => {
    const initializePage = async () => {
      setLoading(true);
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [setLoading]);

  const getBadgeClass = (badge?: string) => {
    if (!badge) return '';
    const badges = {
      Popular: 'bg-gradient-to-r from-blue-500 to-blue-600',
      New: 'bg-gradient-to-r from-green-500 to-green-600',
      Beta: 'bg-gradient-to-r from-orange-500 to-orange-600',
    };
    return badges[badge as keyof typeof badges] || '';
  };

  const getGradientClass = (color: string) => {
    const gradients = {
      blue: 'from-blue-500/20 to-blue-500/0',
      purple: 'from-purple-500/20 to-purple-500/0',
      orange: 'from-orange-500/20 to-orange-500/0',
      green: 'from-green-500/20 to-green-500/0',
      pink: 'from-pink-500/20 to-pink-500/0',
      yellow: 'from-yellow-500/20 to-yellow-500/0',
    };
    return gradients[color as keyof typeof gradients] || gradients.blue;
  };

  const getIconColorClass = (color: string) => {
    const colors = {
      blue: 'text-blue-400',
      purple: 'text-purple-400',
      orange: 'text-orange-400',
      green: 'text-green-400',
      pink: 'text-pink-400',
      yellow: 'text-yellow-400',
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-900 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-800 via-gray-900 to-black -z-10" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-800/20 to-gray-800/40 -z-10" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-gradient-to-b from-gray-900/80 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-8">
              <Link
                to="/"
                className="flex items-center gap-2 text-xl font-bold text-white hover:text-blue-400 transition-colors group"
              >
                <SparklesIcon className="h-8 w-8 text-blue-500 group-hover:text-blue-400 transition-colors" />
                <span className="bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
                  NEXON AI
                </span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative">
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center opacity-50">
            <div className="w-[500px] h-[500px] bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-3xl ml-20 animate-pulse delay-1000"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-5xl sm:text-6xl font-bold text-white mb-8 leading-tight">
              Experience the Future with{' '}
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 text-transparent bg-clip-text">
                NEXON AI
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-12">
              Unlock the power of artificial intelligence with our cutting-edge platform.
              Create, innovate, and build amazing things with AI.
            </p>
            <div className="flex items-center justify-center gap-6">
              <Link
                to="/generation"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-500 hover:to-purple-500 transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-500/25"
              >
                Get Started
              </Link>
              <Link
                to="/learn-more"
                className="px-8 py-4 bg-gray-800 text-gray-300 font-semibold rounded-lg hover:bg-gray-700 hover:text-white transition-all duration-300 ring-1 ring-gray-700 hover:ring-gray-600"
              >
                Learn More
              </Link>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="relative py-24 bg-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-3xl font-bold text-white mb-16 text-center">
              AI-Powered Features
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature) => (
                <Link
                  key={feature.name}
                  to={feature.link}
                  className="group relative overflow-hidden rounded-xl border border-gray-800 bg-gray-800/50 p-8 backdrop-blur-sm transition-all duration-300 hover:border-gray-700 hover:bg-gray-800/70 hover:scale-[1.02] hover:shadow-xl hover:shadow-black/25"
                >
                  {feature.badge && (
                    <div className={`absolute top-6 right-6 px-3 py-1 text-xs font-semibold text-white rounded-full shadow-lg ${getBadgeClass(feature.badge)}`}>
                      {feature.badge}
                    </div>
                  )}
                  <div className={`absolute top-0 right-0 h-64 w-64 rounded-full blur-3xl -z-10 bg-gradient-to-br ${getGradientClass(feature.color)} opacity-25 group-hover:opacity-50 transition-opacity duration-300`} />
                  <div className="relative flex flex-col h-full">
                    <div className="flex items-center gap-4 mb-6">
                      <div className={`p-3.5 rounded-xl ${getIconColorClass(feature.color)} bg-gray-900/50 ring-1 ring-white/10 group-hover:ring-white/20 transition-all duration-300 group-hover:scale-110`}>
                        <feature.icon className="h-7 w-7" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">{feature.name}</h3>
                    </div>
                    <p className="text-gray-300 text-base mb-6 flex-grow leading-relaxed">{feature.description}</p>
                    <div className="flex items-center text-sm font-medium text-gray-400 group-hover:text-white transition-colors duration-200">
                      Try Now
                      <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="relative py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl border border-gray-800 bg-gray-800/30 backdrop-blur-sm p-12 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-800/10 to-gray-800/30 -z-10" />
              
              <h3 className="text-3xl font-bold text-white mb-16 text-center">
                Why Choose NEXON AI?
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                {benefits.map((benefit) => (
                  <div key={benefit.name} className="relative group">
                    <div className={`p-5 rounded-xl ${getIconColorClass(benefit.color)} bg-gray-900/50 mb-6 inline-block ring-1 ring-white/10 group-hover:ring-white/20 transition-all duration-300 group-hover:scale-110`}>
                      <benefit.icon className="h-8 w-8" />
                    </div>
                    <h4 className="text-xl font-semibold text-white mb-4">{benefit.name}</h4>
                    <p className="text-gray-300 text-base leading-relaxed">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Landing;
